import {catchError} from 'rxjs/operators';
import {ResourcePageRequestOptions} from '../../core/services/resource-page-state.service';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { QueryParamsApiModel } from '../../api/models/query-params-api.model';
import { FilterParameterApiModel } from '../../api/models/filter-parameter.api.model';
import { map, mergeMap, tap } from 'rxjs/operators';
import { ResultListApiModel } from '../../api/models/result-list.api.model';
import { MediaObjectViewModel } from '../../core/models/media-object.view-model';
import { MediaObjectApiModel } from '../../api/models/media-object.api-model';
import { EventRestService } from '../../api/services/event.rest.service';
import { EventApiModel } from '../../api/models/event.api.model';
import { ParticipantApiModel } from '../../api/models/participant.api.model';
import { ParticipantRestService } from '../../api/services/participant.rest.service';
import { plainToClass } from 'class-transformer';
import { MediaObjectRestService } from '../../api/services/media-object.rest.service';
import { PostRestService } from '../../api/services/post-rest.service';
import { PostApiModel } from '../../api/models/post-api.model';
import { PostViewModel } from '../../shared/timeline/post.view-model';
import { UserViewModel } from '../user/settings/user.view-model';
import { FollowApiModel } from "../../api/models/follow.api-model";
import { FollowTypeEnum } from "../../core/enums/follow-type.enum";
import { FollowerRestService } from "../../api/services/follower.rest.service";
import { HttpHeaders, HttpResponse } from "@angular/common/http";
import { ReactionApiModel } from "../../api/models/reaction.api.model";
import { ReactionTypeEnum } from "../../core/enums/reaction-type.enum";
import { ReactionRestService } from "../../api/services/reaction.rest.service";
import { ReactedWithEnum } from "../../core/enums/reacted-with.enum";
import { CurrentUserViewModel } from "../../core/models/current-user.view-model";
import { TokenDataProviderService } from "../../core/services/token-data-provider.service";
import { UserRestService } from "../../api/services/user.rest.service";
import { ProjectRestService } from '../../api/services/project.rest.service';
import { ProjectApiModel } from '../../api/models/project.api-model';
import { EventAttendanceDeclarationRestService } from '../../api/services/event-attendance-declaration.rest.service';
import { EventParticipationRequestRestService } from '../../api/services/event-participation-request.rest.service';
import { EventParticipationRequestApiModel } from '../../api/models/event-participation-request.api-model';
import { CacheRefreshService } from '../../core/services/cache-refresh.service';
import {FeatureFlagService} from '../../core/feature-flags/feature-flag.service';
import {FeatureFlag} from '../../core/feature-flags/feature-flag.types';

@Injectable({
  providedIn: 'root'
})
export class EventProviderService {
  public totalItems: number = 0;

  constructor(
    private readonly eventRS: EventRestService,
    private readonly participantRS: ParticipantRestService,
    private readonly mediaRS: MediaObjectRestService,
    private readonly postRS: PostRestService,
    private readonly followerRS: FollowerRestService,
    private readonly reactionRS: ReactionRestService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly userRS: UserRestService,
    private readonly projectRS: ProjectRestService,
    private readonly eventAttendanceDeclarationRS: EventAttendanceDeclarationRestService,
    private readonly eventParticipationRequestRS: EventParticipationRequestRestService,
    private readonly cacheRefresh: CacheRefreshService,
    private readonly featureFlags: FeatureFlagService,
  ) { }

  public getCollectionPage(queryParams: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<EventApiModel>> {
    return this.eventRS.getCollection(this.withGuestImportedEventsExcluded(queryParams));
  }

  public getCollection(queryParams: QueryParamsApiModel = new QueryParamsApiModel()): Observable<EventApiModel[]> {
    return this.getCollectionPage(queryParams).pipe(map((result) => {
      this.totalItems = result.total;
      return result.records;
    }));
  }

  private withGuestImportedEventsExcluded(queryParams: QueryParamsApiModel): QueryParamsApiModel {
    const resolvedQueryParams = Object.assign(new QueryParamsApiModel(), queryParams);
    resolvedQueryParams.filters = [...(queryParams.filters || [])];

    const isAuthenticated = this.tokenDPS.isLoggedIn() || this.tokenDPS.isLoggedInSSR();
    if (isAuthenticated || !this.featureFlags.isEnabled(FeatureFlag.GUEST_EVENTS_EXCLUDE_IMPORTED)) {
      return resolvedQueryParams;
    }

    resolvedQueryParams.filters = resolvedQueryParams.filters.filter((filter) => filter.name !== 'excludeImported');
    resolvedQueryParams.filters.push(new FilterParameterApiModel('excludeImported', 'true', 'single'));

    return resolvedQueryParams;
  }


  public getMyEventsCollection(queryParams: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<EventApiModel>> {
    return this.tokenDPS.currentUser.pipe(
      mergeMap((currentUserVM: CurrentUserViewModel) => {
        if (!currentUserVM?.['@id']) {
          return of(new ResultListApiModel<ParticipantApiModel>(ParticipantApiModel));
        }
        const incomingQueryParams = Object.assign(new QueryParamsApiModel(), queryParams);
        incomingQueryParams.filters = [
          ...queryParams.filters,
          new FilterParameterApiModel('participants', currentUserVM['@id'], 'array'),
        ];
        return this.eventRS.getCollection(incomingQueryParams).pipe(
          tap((resultListAM: ResultListApiModel<EventApiModel>) => {
            this.totalItems = resultListAM.total;
          })
        );
      }),
      map((resultListAM: ResultListApiModel<EventApiModel> | ResultListApiModel<ParticipantApiModel>) => {
        const records = (resultListAM as ResultListApiModel<any>)?.records ?? [];
        if (records.length && records[0]?.event) {
          const empty = new ResultListApiModel<EventApiModel>(EventApiModel);
          empty.records = [];
          empty.total = 0;
          this.totalItems = 0;
          return empty;
        }

        const eventsResult = resultListAM as ResultListApiModel<EventApiModel>;
        eventsResult.records = this.dedupeEvents(eventsResult.records || []);
        this.totalItems = eventsResult.total ?? eventsResult.records?.length ?? 0;
        return eventsResult;
      }));
  }

  private dedupeEvents(events: EventApiModel[]): EventApiModel[] {
    if (!events?.length) {
      return [];
    }
    const byId = new Map<string, EventApiModel>();
    events.forEach(eventAM => {
      if (eventAM?.['@id']) {
        byId.set(eventAM['@id'], eventAM);
      }
    });
    return Array.from(byId.values());
  }

  public getEvent(slug: string, publicPreview = !(this.tokenDPS.isLoggedIn() || this.tokenDPS.isLoggedInSSR()), options?: ResourcePageRequestOptions): Observable<EventApiModel> {
    return this.eventRS.get(slug, options).pipe(mergeMap((eventAM: EventApiModel) => {
      if (publicPreview || eventAM?.invitationStatus === 'PENDING') {
        return of(eventAM);
      }
      return forkJoin([
        this.getAllEventFollowsCollection(eventAM).pipe(catchError(() => of(null))),
        this.getAllEventReactionsCollection(eventAM).pipe(catchError(() => of(null)))
      ]).pipe(map(() => {
        return eventAM;
      }));
    }));
  }
  public participateEvent(eventAM: EventApiModel, state: string, participantAM?: ParticipantApiModel): Observable<ParticipantApiModel> {
    if (!participantAM) {
      participantAM = plainToClass(ParticipantApiModel, { event: eventAM['@id'], state, type: 'PRIVATE' });
    }
    const tags = this.mergeTags(
      ['participants', 'participations', 'events'],
      this.buildEventTags(eventAM),
      this.buildUserTags()
    );
    return this.participantRS.create(participantAM, this.buildInvalidateOptions(tags));
  }

  public removeParticipation(participantAM: ParticipantApiModel): Observable<any> {
    const eventTags = this.buildEventTagsFromParticipant(participantAM);
    const tags = this.mergeTags(
      ['participants', 'participations', 'events'],
      eventTags,
      this.buildUserTags()
    );
    return this.participantRS.delete(participantAM, this.buildInvalidateOptions(tags));
  }

  public declareAttendance(
    eventAM: EventApiModel,
    projectIris: string[],
    participantType: 'PRIVATE' | 'BUSINESS' = 'PRIVATE',
    representedCompany: string | null = null
  ): Observable<EventApiModel> {
    const payload = {
      participantType,
      representedCompany,
      projects: projectIris || []
    };

    const request$ = eventAM?.attendanceDeclarationId
      ? this.eventAttendanceDeclarationRS.update(eventAM.attendanceDeclarationId, payload)
      : this.eventAttendanceDeclarationRS.create({
        event: eventAM['@id'],
        participantType,
        representedCompany,
        projects: projectIris || []
      });

    this.cacheRefresh.requestRefresh();
    return request$.pipe(mergeMap(() => this.getEvent(eventAM.slug)));
  }

  public cancelAttendanceDeclaration(eventAM: EventApiModel): Observable<EventApiModel> {
    if (!eventAM?.attendanceDeclarationId) {
      return of(eventAM);
    }

    this.cacheRefresh.requestRefresh();
    return this.eventAttendanceDeclarationRS.cancel(eventAM.attendanceDeclarationId)
      .pipe(mergeMap(() => this.getEvent(eventAM.slug)));
  }

  public getCurrentUserProjectsForAttendance(): Observable<ProjectApiModel[]> {
    return this.tokenDPS.currentUser.pipe(mergeMap((currentUserVM: CurrentUserViewModel) => {
      if (!currentUserVM?.['@id']) {
        return of([]);
      }

      return this.projectRS.getUserSubResourceCollection(currentUserVM['@id']).pipe(map((resultListAM: ResultListApiModel<ProjectApiModel>) => {
        return resultListAM.records || [];
      }));
    }));
  }

  public getProjectParticipationEvents(projectId: number): Observable<{ upcoming: EventApiModel[]; past: EventApiModel[] }> {
    if (!projectId) {
      return of({upcoming: [], past: []});
    }

    const requestParams = new QueryParamsApiModel();
    requestParams.pagination = false;
    requestParams.filters.push(new FilterParameterApiModel('vehicles.project.id', projectId, 'single'));
    requestParams.filters.push(new FilterParameterApiModel('status', 'APPROVED', 'single'));

    return this.eventParticipationRequestRS.getCollection(requestParams).pipe(mergeMap((resultList) => {
      const eventSlugs = this.extractEventSlugsFromRequests(resultList.records || []);
      if (!eventSlugs.length) {
        return of({upcoming: [], past: []});
      }

      const eventQueryParams = new QueryParamsApiModel();
      eventQueryParams.pagination = false;
      eventSlugs.forEach((slug) => eventQueryParams.filters.push(new FilterParameterApiModel('slug', slug, 'array')));

      return this.eventRS.getCollection(eventQueryParams).pipe(map((eventResultList) => {
        const now = Date.now();
        const deduped = this.dedupeEvents(eventResultList.records || []);
        const upcoming = deduped
          .filter((eventAM) => this.resolveEventEndAt(eventAM) >= now)
          .sort((left, right) => this.resolveEventStartAt(left) - this.resolveEventStartAt(right));
        const past = deduped
          .filter((eventAM) => this.resolveEventEndAt(eventAM) < now)
          .sort((left, right) => this.resolveEventEndAt(right) - this.resolveEventEndAt(left));

        return {upcoming, past};
      }));
    }));
  }

  private extractEventSlugsFromRequests(requests: EventParticipationRequestApiModel[]): string[] {
    const slugs = new Set<string>();

    requests.forEach((request) => {
      const declaration = request?.declaration as any;
      const eventRef = declaration?.event;
      const slug = this.extractEventSlug(eventRef);
      if (slug) {
        slugs.add(slug);
      }
    });

    return Array.from(slugs.values());
  }

  public getMediaPosts(postTypes: string[], eventAM: EventApiModel, limit: number): Observable<PostViewModel[]> {
    if (!(this.tokenDPS.isLoggedIn() || this.tokenDPS.isLoggedInSSR())) {
      return of([]);
    }
    const params = new QueryParamsApiModel();
    postTypes.forEach((postType: string) => {
      params.filters.push(new FilterParameterApiModel('discriminator', postType, 'array'));

    });
    params.filters.push(new FilterParameterApiModel('event', eventAM.id, 'single'));

    if (limit) {
      params.limit = limit;
    }

    if (params.filters.length) {
      return this.postRS.getCollection(params).pipe(map((resultListAM: ResultListApiModel<PostApiModel>) => {
        return resultListAM.records.map((postAM: PostApiModel) => {
          const postVM = new PostViewModel(postAM);
          const userVM = new UserViewModel(postAM.postedBy);
          userVM.avatar = postAM.postedBy.avatar ? new MediaObjectViewModel(postAM.postedBy.avatar as MediaObjectApiModel) : null;
          postVM.postedBy = userVM;
          return postVM;
        });
      }));
    }

    return of<PostViewModel[]>([]);
  }

  public postFollow(eventAM: EventApiModel): Observable<EventApiModel> {
    return this.followerRS.create(plainToClass(FollowApiModel, { followed: eventAM['@id'], type: FollowTypeEnum.EVENT }))
      .pipe(map((followAM: FollowApiModel) => {
        eventAM.follows.unshift(followAM);
        return eventAM;
      }));
  }

  public removeFollow(followAM: FollowApiModel, eventAM: EventApiModel): Observable<EventApiModel> {
    return this.followerRS.delete(followAM).pipe(map((e: HttpResponse<any>) => {
      eventAM.follows = eventAM.follows.filter(x => x.followedBy !== followAM.followedBy);
      return eventAM;
    }));
  }

  public getAllEventFollowsCollection(eventAM: EventApiModel): Observable<FollowApiModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.eventRS.getFollowsSubResourceCollection(eventAM, params).pipe(map((resultListAM: ResultListApiModel<FollowApiModel>) => {
      eventAM.follows = resultListAM.records;
      return resultListAM.records;
    }));
  }

  public postReaction(eventAM: EventApiModel): Observable<EventApiModel> {
    const tags = this.mergeTags(
      ['reactions', 'received_reactions', 'events'],
      this.buildEventTags(eventAM),
      this.buildUserTags()
    );
    return this.reactionRS.create(
      plainToClass(ReactionApiModel, { subject: eventAM['@id'], reactedWith: ReactedWithEnum.THUMBS_UP, type: ReactionTypeEnum.EVENT_REACTION }),
      this.buildInvalidateOptions(tags)
    )
      .pipe(map((reactionAM: ReactionApiModel) => {
        eventAM.reactions.unshift(reactionAM);
        return eventAM;
      }));
  }

  public removeReaction(reactionAM: ReactionApiModel, eventAM: EventApiModel): Observable<EventApiModel> {
    const tags = this.mergeTags(
      ['reactions', 'received_reactions', 'events'],
      this.buildEventTags(eventAM),
      this.buildUserTags()
    );
    return this.reactionRS.delete(reactionAM, this.buildInvalidateOptions(tags)).pipe(map((e: HttpResponse<any>) => {
      eventAM.reactions = eventAM.reactions.filter(x => x.author !== reactionAM.author);
      return eventAM;
    }));
  }

  private buildEventTags(eventAM: EventApiModel): string[] {
    const tags: string[] = [];
    if (eventAM?.id) {
      tags.push(`events:${eventAM.id}`);
    }
    if (eventAM?.slug) {
      tags.push(`events:${eventAM.slug}`);
    }
    return tags;
  }

  private buildEventTagsFromParticipant(participantAM: ParticipantApiModel): string[] {
    const tags: string[] = [];
    const event = participantAM?.event;
    if (!event) {
      return tags;
    }
    if (typeof event === 'string') {
      const parts = event.split('/').filter(Boolean);
      const id = parts[parts.length - 1];
      if (id) {
        tags.push(`events:${id}`);
      }
      return tags;
    }
    if (event.id) {
      tags.push(`events:${event.id}`);
    }
    if (event.slug) {
      tags.push(`events:${event.slug}`);
    }
    return tags;
  }

  private buildUserTags(): string[] {
    const user = this.tokenDPS.getJwtUser();
    if (!user?.id && !user?.slug) {
      return [];
    }
    const tags: string[] = [];
    if (user?.id) {
      tags.push(`users:${user.id}`, `users:${user.id}:participations`, `users:${user.id}:received_reactions`);
    }
    if (user?.slug) {
      tags.push(`users:${user.slug}`, `users:${user.slug}:participations`, `users:${user.slug}:received_reactions`);
    }
    return tags;
  }

  private extractEventSlug(eventRef: EventApiModel | string | null | undefined): string | null {
    if (!eventRef) {
      return null;
    }

    if (typeof eventRef === 'string') {
      const parts = eventRef.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }

    if (eventRef.slug) {
      return eventRef.slug;
    }

    if (eventRef['@id']) {
      const parts = String(eventRef['@id']).split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }

    return null;
  }

  private resolveEventEndAt(eventAM: EventApiModel): number {
    if (!eventAM?.eventDates?.length) {
      return 0;
    }

    const sorted = [...eventAM.eventDates].sort((left, right) => {
      const leftEnd = new Date(left?.end || left?.to || left?.start || left?.from || 0).getTime();
      const rightEnd = new Date(right?.end || right?.to || right?.start || right?.from || 0).getTime();
      return leftEnd - rightEnd;
    });

    const last = sorted[sorted.length - 1];
    return new Date(last?.end || last?.to || last?.start || last?.from || 0).getTime();
  }

  private resolveEventStartAt(eventAM: EventApiModel): number {
    if (!eventAM?.eventDates?.length) {
      return Number.MAX_SAFE_INTEGER;
    }

    const sorted = [...eventAM.eventDates].sort((left, right) => {
      const leftStart = new Date(left?.start || left?.from || left?.end || left?.to || 0).getTime();
      const rightStart = new Date(right?.start || right?.from || right?.end || right?.to || 0).getTime();
      return leftStart - rightStart;
    });

    const first = sorted[0];
    return new Date(first?.start || first?.from || first?.end || first?.to || 0).getTime();
  }

  private buildInvalidateOptions(tags: string[]): {headers: HttpHeaders} | undefined {
    if (!tags.length) {
      return undefined;
    }
    return {
      headers: new HttpHeaders({'x-cache-invalidate': tags.join(',')})
    };
  }

  private mergeTags(...tagGroups: string[][]): string[] {
    const tags: string[] = [];
    tagGroups.forEach((group) => {
      group.forEach((tag) => {
        if (tag && !tags.includes(tag)) {
          tags.push(tag);
        }
      });
    });
    return tags;
  }

  public getAllEventReactionsCollection(eventAM: EventApiModel): Observable<ReactionApiModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.eventRS.getReactionsSubResourceCollection(eventAM, params).pipe(map((resultListAM: ResultListApiModel<ReactionApiModel>) => {
      eventAM.reactions = resultListAM.records;
      return resultListAM.records;
    }));
  }
}
